import axios from 'axios';
import { apiDoublePrefixGuard } from '@/lib/axiosGuards';

// IMPORTANT API CONVENTION
// ────────────────────────────────────────────────────────────────────
// `API_URL` already includes the '/api' prefix. When using the shared
// axios client, ALL paths must be RELATIVE to '/api' — i.e. start with a
// single '/' and NOT with '/api/'.
//
//   ✅ api.get('/incidents')
//   ❌ api.get('/api/incidents')      // becomes /api/api/incidents → 404
//
// `apiDoublePrefixGuard` (request interceptor below) auto-corrects the
// bad pattern and emits a console.error in dev so it surfaces immediately.
const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const createApiClient = (token) => {
  const client = axios.create({
    baseURL: API_URL,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });

  client.interceptors.request.use(apiDoublePrefixGuard);

  client.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
  );

  return client;
};

// Dashboard
export const getDashboardStats = (api) => api.get('/dashboard/stats');
export const getAIAnalysis = (api) => api.get('/dashboard/ai-analysis');

// Incidents
export const getIncidents = (api, params) => api.get('/incidents', { params });
export const getIncident = (api, id) => api.get(`/incidents/${id}`);
export const createIncident = (api, data) => api.post('/incidents', data);
export const updateIncident = (api, id, data) => api.put(`/incidents/${id}`, data);
export const runIncidentAIAnalysis = (api, id) => api.post(`/incidents/${id}/ai-analysis`);
export const runRootCauseAnalysis = (api, id) => api.post(`/incidents/${id}/root-cause-analysis`);

// ECCAIRS 2 Compatibility
export const mapIncidentToECCAIRS = (api, id) => api.post(`/incidents/${id}/eccairs/map`);
export const updateECCAIRSData = (api, id, data) => api.put(`/incidents/${id}/eccairs`, data);
export const exportECCAIRSFormat = (api, id) => api.get(`/incidents/${id}/eccairs/export`);
export const getECCAIRSTaxonomy = (api) => api.get('/eccairs/taxonomy');

// Risks
export const getRisks = (api, params) => api.get('/risks', { params });
export const getRisk = (api, id) => api.get(`/risks/${id}`);
export const createRisk = (api, data) => api.post('/risks', data);
export const updateRisk = (api, id, data) => api.put(`/risks/${id}`, data);
export const runRiskAIAnalysis = (api, id) => api.post(`/risks/${id}/ai-analysis`);

// Audits
export const getAudits = (api, params) => api.get('/audits', { params });
export const getAudit = (api, id) => api.get(`/audits/${id}`);
export const createAudit = (api, data) => api.post('/audits', data);
export const updateAudit = (api, id, data) => api.put(`/audits/${id}`, data);
export const addAuditFinding = (api, id, data) => api.post(`/audits/${id}/findings`, data);

// IOSA Audit Templates
export const getIOSATemplates = (api) => api.get('/iosa/templates');
export const getIOSATemplate = (api, section) => api.get(`/iosa/templates/${section}`);
export const createAuditFromIOSA = (api, section, title, scheduledDate) => 
  api.post('/iosa/audits/create-from-template', null, { params: { section, title, scheduled_date: scheduledDate } });
export const updateIOSAChecklistItem = (api, auditId, itemId, status, finding, evidence, notes) =>
  api.put(`/iosa/audits/${auditId}/checklist/${itemId}`, null, { params: { status, finding, evidence, auditor_notes: notes } });
export const getIOSAAuditProgress = (api, auditId) => api.get(`/iosa/audits/${auditId}/progress`);

// CAPAs
export const getCAPAs = (api, params) => api.get('/capas', { params });
export const getCAPA = (api, id) => api.get(`/capas/${id}`);
export const createCAPA = (api, data) => api.post('/capas', data);
export const updateCAPA = (api, id, data) => api.put(`/capas/${id}`, data);

// Policies
export const getPolicies = (api, params) => api.get('/policies', { params });
export const getPolicy = (api, id) => api.get(`/policies/${id}`);
export const createPolicy = (api, data) => api.post('/policies', data);
export const updatePolicy = (api, id, data) => api.put(`/policies/${id}`, data);

// Policy Revision History
export const getPolicyRevisions = (api, policyId, limit = 50) => 
  api.get(`/policies/${policyId}/revisions`, { params: { limit } });
export const getPolicyRevision = (api, policyId, version) => 
  api.get(`/policies/${policyId}/revisions/${version}`);
export const comparePolicyRevisions = (api, policyId, versionFrom, versionTo) => 
  api.get(`/policies/${policyId}/revisions/compare`, { params: { version_from: versionFrom, version_to: versionTo } });
export const restorePolicyVersion = (api, policyId, version, changeReason) => 
  api.post(`/policies/${policyId}/restore/${version}`, null, { params: { change_reason: changeReason } });

// Training
export const getTrainingPrograms = (api, params) => api.get('/training/programs', { params });
export const createTrainingProgram = (api, data) => api.post('/training/programs', data);
export const getTrainingSessions = (api, params) => api.get('/training/sessions', { params });
export const createTrainingSession = (api, data) => api.post('/training/sessions', data);
export const getTrainingSession = (api, id) => api.get(`/training/sessions/${id}`);
export const addSessionParticipant = (api, sessionId, userId) => api.post(`/training/sessions/${sessionId}/participants`, null, { params: { user_id: userId } });
export const removeSessionParticipant = (api, sessionId, userId) => api.delete(`/training/sessions/${sessionId}/participants/${userId}`);
export const updateSessionAttendance = (api, sessionId, attendance) => api.put(`/training/sessions/${sessionId}/attendance`, attendance);
export const updateSessionStatus = (api, sessionId, status) => api.put(`/training/sessions/${sessionId}/status`, null, { params: { status } });
export const recordCompletion = (api, sessionId, userId, score = 100) => api.put(`/training/sessions/${sessionId}/completion`, null, { params: { user_id: userId, score } });
export const getUserTrainingRecords = (api, userId) => api.get(`/training/user/${userId}/records`);
export const downloadTrainingCertificate = (token, sessionId, userId) => {
  return axios.get(`${API_URL}/training/sessions/${sessionId}/certificate/${userId}`, {
    headers: { Authorization: `Bearer ${token}` },
    responseType: 'blob'
  });
};

// SPIs
export const getSPIs = (api, params) => api.get('/spis', { params });
export const createSPI = (api, data) => api.post('/spis', data);
export const addSPIData = (api, id, params) => api.post(`/spis/${id}/data`, null, { params });
export const getSPIData = (api, id, params) => api.get(`/spis/${id}/data`, { params });

// Compliance
export const getRegulatoryClauses = (api, params) => api.get('/compliance/clauses', { params });
export const getComplianceEvidence = (api, params) => api.get('/compliance/evidence', { params });
export const createComplianceEvidence = (api, data) => api.post('/compliance/evidence', data);
export const updateComplianceEvidence = (api, id, data) => api.put(`/compliance/evidence/${id}`, data);
export const getComplianceStatus = (api) => api.get('/compliance/status');

// Reports
export const downloadReport = (api, reportType) => api.get(`/reports/${reportType}/pdf`, { responseType: 'blob' });

// Safety Bulletins
export const getBulletins = (api, params) => api.get('/bulletins', { params });
export const getBulletin = (api, id) => api.get(`/bulletins/${id}`);
export const createBulletin = (api, data) => api.post('/bulletins', data);
export const updateBulletin = (api, id, data) => api.put(`/bulletins/${id}`, data);
export const acknowledgeBulletin = (api, id) => api.post(`/bulletins/${id}/acknowledge`);
export const deleteBulletin = (api, id) => api.delete(`/bulletins/${id}`);
export const getUnreadBulletinCount = (api) => api.get('/bulletins/unread-count');

// Safety Meetings
export const getMeetings = (api, params) => api.get('/meetings', { params });
export const getMeeting = (api, id) => api.get(`/meetings/${id}`);
export const createMeeting = (api, data) => api.post('/meetings', data);
export const updateMeeting = (api, id, data) => api.put(`/meetings/${id}`, data);
export const startMeeting = (api, id) => api.post(`/meetings/${id}/start`);
export const completeMeeting = (api, id, params) => api.post(`/meetings/${id}/complete`, null, { params });
export const cancelMeeting = (api, id) => api.post(`/meetings/${id}/cancel`);
export const addMeetingAttendee = (api, meetingId, userId) => api.post(`/meetings/${meetingId}/attendees`, null, { params: { user_id: userId } });
export const removeMeetingAttendee = (api, meetingId, userId) => api.delete(`/meetings/${meetingId}/attendees/${userId}`);
export const addMeetingActionItem = (api, meetingId, params) => api.post(`/meetings/${meetingId}/action-items`, null, { params });
export const updateMeetingActionItem = (api, meetingId, actionId, params) => api.put(`/meetings/${meetingId}/action-items/${actionId}`, null, { params });
export const getUpcomingMeetingsCount = (api) => api.get('/meetings/upcoming/count');

// Users
export const getUsers = (api, params) => api.get('/users', { params });
export const getUser = (api, id) => api.get(`/users/${id}`);
export const updateUser = (api, id, data) => api.put(`/users/${id}`, data);

// User Notification Preferences
export const getNotificationPreferences = (api) => api.get('/users/me/notification-preferences');
export const updateNotificationPreferences = (api, data) => api.put('/users/me/notification-preferences', data);
export const resetNotificationPreferences = (api) => api.post('/users/me/notification-preferences/reset');

// In-App Notifications
export const getNotifications = (api, params = {}) => api.get('/notifications', { params });
export const getUnreadNotificationCount = (api) => api.get('/notifications/unread-count');
export const markNotificationRead = (api, id) => api.put(`/notifications/${id}/read`);
export const markAllNotificationsRead = (api) => api.put('/notifications/read-all');
export const deleteNotification = (api, id) => api.delete(`/notifications/${id}`);
export const sendTestNotification = (api) => api.post('/notifications/test');

// Settings
export const getOrganizationSettings = (api) => api.get('/settings/organization');
export const updateOrganizationSettings = (api, params) => api.put('/settings/organization', null, { params });
export const toggleInspectionMode = (api, params) => api.post('/settings/inspection-mode', null, { params });

// Seed Data
export const seedRegulatoryClauses = (api) => api.post('/seed/regulatory-clauses');
export const seedDemoData = (api) => api.post('/seed/demo-data');

// Reports (PDF download)
export const downloadIncidentPDF = (token, id) => {
  return axios.get(`${API_URL}/reports/incident/${id}/pdf`, {
    headers: { Authorization: `Bearer ${token}` },
    responseType: 'blob'
  });
};

export const downloadRiskRegisterPDF = (token) => {
  return axios.get(`${API_URL}/reports/risks/pdf`, {
    headers: { Authorization: `Bearer ${token}` },
    responseType: 'blob'
  });
};

export const downloadAuditPDF = (token, id) => {
  return axios.get(`${API_URL}/reports/audit/${id}/pdf`, {
    headers: { Authorization: `Bearer ${token}` },
    responseType: 'blob'
  });
};

export const downloadSPIPDF = (token) => {
  return axios.get(`${API_URL}/reports/spis/pdf`, {
    headers: { Authorization: `Bearer ${token}` },
    responseType: 'blob'
  });
};

export const downloadCompliancePDF = (token, authority) => {
  return axios.get(`${API_URL}/reports/compliance/${authority}/pdf`, {
    headers: { Authorization: `Bearer ${token}` },
    responseType: 'blob'
  });
};

// Change Management
export const getChanges = (api, params) => api.get('/changes', { params });
export const getChange = (api, id) => api.get(`/changes/${id}`);
export const createChange = (api, data) => api.post('/changes', data);
export const updateChange = (api, id, data) => api.put(`/changes/${id}`, data);
export const submitChange = (api, id) => api.post(`/changes/${id}/submit`);
export const startChangeReview = (api, id) => api.post(`/changes/${id}/review`);
export const approveChange = (api, id, params) => api.post(`/changes/${id}/approve`, null, { params });
export const startChangeImplementation = (api, id) => api.post(`/changes/${id}/start-implementation`);
export const completeChange = (api, id) => api.post(`/changes/${id}/complete`);
export const closeChange = (api, id) => api.post(`/changes/${id}/close`);
export const addChangeRiskAssessment = (api, id, params) => api.post(`/changes/${id}/risk-assessment`, null, { params });
export const addChangeImplementationStep = (api, id, params) => api.post(`/changes/${id}/implementation-step`, null, { params });
export const updateChangeImplementationStep = (api, changeId, stepId, params) => api.put(`/changes/${changeId}/implementation-step/${stepId}`, null, { params });
export const getChangeStats = (api) => api.get('/changes/stats/summary');

// Predictive Analytics
export const getEarlyWarnings = (api, lookbackDays = 90) => api.get('/analytics/early-warnings', { params: { lookback_days: lookbackDays } });
export const getHazardClusters = (api, minClusterSize = 3) => api.get('/analytics/hazard-clusters', { params: { min_cluster_size: minClusterSize } });
export const getRiskForecast = (api, riskId) => api.get(`/analytics/risk-forecast/${riskId}`);
export const getWorkloadForecast = (api, daysAhead = 30) => api.get('/analytics/workload-forecast', { params: { days_ahead: daysAhead } });
export const getSPITrends = (api) => api.get('/analytics/spi-trends');

// Risk & Trend Analytics
export const getRiskTrends = (api, months = 12) => api.get('/dashboard/risk-trends', { params: { months } });
export const getIncidentTrends = (api, months = 12) => api.get('/dashboard/incident-trends', { params: { months } });
export const getCAPATrends = (api, months = 12) => api.get('/dashboard/capa-trends', { params: { months } });

// Scheduled Reports
export const getScheduledReports = (api) => api.get('/scheduled-reports');
export const createScheduledReport = (api, params) => api.post('/scheduled-reports', null, { params });
export const deleteScheduledReport = (api, reportId) => api.delete(`/scheduled-reports/${reportId}`);
export const getScheduledReportLogs = (api, limit = 50) => api.get('/scheduled-reports/logs', { params: { limit } });

// Safety Culture Surveys
export const getSurveys = (api, isActive) => api.get('/surveys', { params: { is_active: isActive } });
export const createSurvey = (api, params) => api.post('/surveys', null, { params });
export const addSurveyQuestion = (api, surveyId, params) => api.post(`/surveys/${surveyId}/questions`, null, { params });
export const submitSurveyResponse = (api, surveyId, answers) => api.post(`/surveys/${surveyId}/responses`, { answers });
export const getSurveyResults = (api, surveyId) => api.get(`/surveys/${surveyId}/results`);

// Training Assessments
export const getAssessments = (api, programId) => api.get(`/training/programs/${programId}/assessments`);
export const createAssessment = (api, programId, params) => api.post(`/training/programs/${programId}/assessments`, null, { params });
export const addAssessmentQuestion = (api, assessmentId, params) => api.post(`/training/assessments/${assessmentId}/questions`, null, { params });
export const startAssessmentAttempt = (api, assessmentId) => api.post(`/training/assessments/${assessmentId}/attempt`);
export const submitAssessmentAttempt = (api, attemptId, answers) => api.post(`/training/assessments/attempts/${attemptId}/submit`, { answers });

// CAPA Closure Validation
export const addCAPAClosureItem = (api, capaId, item) => api.post(`/capas/${capaId}/closure-checklist`, null, { params: { item } });
export const verifyCAPAClosureItem = (api, capaId, itemId, params) => api.put(`/capas/${capaId}/closure-checklist/${itemId}`, null, { params });
export const verifyCAPAEffectiveness = (api, capaId, params) => api.post(`/capas/${capaId}/verify-effectiveness`, null, { params });

// Regulator Theming
export const getRegulatorThemes = (api) => api.get('/settings/regulator-themes');
export const setActiveRegulator = (api, regulator) => api.put('/settings/active-regulator', null, { params: { regulator } });

// ==================== COLLABORATION API ====================

// Comments
export const getComments = (api, entityType, entityId, includeReplies = true) => 
  api.get(`/collaboration/comments/${entityType}/${entityId}`, { params: { include_replies: includeReplies } });

export const createComment = (api, data) => api.post('/collaboration/comments', data);

export const getCommentReplies = (api, commentId) => 
  api.get(`/collaboration/comments/${commentId}/replies`);

export const updateComment = (api, commentId, data) => 
  api.patch(`/collaboration/comments/${commentId}`, data);

export const deleteComment = (api, commentId) => 
  api.delete(`/collaboration/comments/${commentId}`);

export const addCommentReaction = (api, commentId, emoji) => 
  api.post(`/collaboration/comments/${commentId}/reactions/${emoji}`);

export const removeCommentReaction = (api, commentId, emoji) => 
  api.delete(`/collaboration/comments/${commentId}/reactions/${emoji}`);

// Evidence Pins
export const getEvidencePins = (api, entityType, entityId, activeOnly = true) => 
  api.get(`/collaboration/pins/${entityType}/${entityId}`, { params: { active_only: activeOnly } });

export const createEvidencePin = (api, data) => api.post('/collaboration/pins', data);

export const updateEvidencePin = (api, pinId, data) => 
  api.patch(`/collaboration/pins/${pinId}`, data);

export const deleteEvidencePin = (api, pinId) => 
  api.delete(`/collaboration/pins/${pinId}`);

export const recordPinView = (api, pinId) => 
  api.post(`/collaboration/pins/${pinId}/view`);

// Presence
export const joinEntityPresence = (api, entityType, entityId, status = 'viewing') => 
  api.post('/collaboration/presence/join', null, { params: { entity_type: entityType, entity_id: entityId, status } });

export const leaveEntityPresence = (api, entityType, entityId) => 
  api.post('/collaboration/presence/leave', null, { params: { entity_type: entityType, entity_id: entityId } });

export const presenceHeartbeat = (api, entityType, entityId, status) => 
  api.post('/collaboration/presence/heartbeat', null, { params: { entity_type: entityType, entity_id: entityId, status } });

export const getActiveUsers = (api, entityType, entityId) => 
  api.get(`/collaboration/presence/${entityType}/${entityId}`);

// Activity Feed
export const getEntityActivities = (api, entityType, entityId, limit = 50) => 
  api.get(`/collaboration/activities/${entityType}/${entityId}`, { params: { limit } });

export const getMyActivities = (api, limit = 100) => 
  api.get('/collaboration/activities/user/me', { params: { limit } });

export const getRecentActivities = (api, hours = 24, entityTypes, limit = 100) => 
  api.get('/collaboration/activities/recent', { params: { hours, entity_types: entityTypes, limit } });

export const markActivityRead = (api, activityId) => 
  api.post(`/collaboration/activities/${activityId}/read`);

// User Search for Mentions
export const searchUsersForMention = (api, query, limit = 10) => 
  api.get('/collaboration/users/search', { params: { q: query, limit } });

// Collaboration Stats
export const getCollaborationStats = (api, entityType, entityId) => 
  api.get(`/collaboration/stats/${entityType}/${entityId}`);

// WebSocket URL helper
export const getCollaborationWSUrl = (entityType, entityId, token) => {
  const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const backendUrl = process.env.REACT_APP_BACKEND_URL.replace(/^https?:/, wsProtocol);
  return `${backendUrl}/ws/collaboration/${entityType}/${entityId}?token=${token}`;
};

// ==================== ASSET REPOSITORY API ====================

// Aircraft
export const getAircraftList = (api, params) => api.get('/aircraft', { params });
export const getAircraftById = (api, id) => api.get(`/aircraft/${id}`);
export const createAircraftAsset = (api, data) => api.post('/aircraft', data);
export const updateAircraftAsset = (api, id, data) => api.put(`/aircraft/${id}`, data);

// Engines
export const getEnginesList = (api, params) => api.get('/engines', { params });
export const getEngineById = (api, id) => api.get(`/engines/${id}`);
export const createEngineAsset = (api, data) => api.post('/engines', data);
export const updateEngineAsset = (api, id, data) => api.put(`/engines/${id}`, data);

// Components
export const getComponentsList = (api, params) => api.get('/components', { params });
export const getComponentById = (api, id) => api.get(`/components/${id}`);
export const createComponentAsset = (api, data) => api.post('/components', data);
export const updateComponentAsset = (api, id, data) => api.put(`/components/${id}`, data);

// Ground Support Equipment
export const getGSEList = (api, params) => api.get('/gse', { params });
export const getGSEById = (api, id) => api.get(`/gse/${id}`);
export const createGSEAsset = (api, data) => api.post('/gse', data);
export const updateGSEAsset = (api, id, data) => api.put(`/gse/${id}`, data);

// Organizations
export const getOrganizationsList = (api, params) => api.get('/organizations', { params });
export const getOrganizationById = (api, id) => api.get(`/organizations/${id}`);
export const createOrganizationAsset = (api, data) => api.post('/organizations', data);
export const updateOrganizationAsset = (api, id, data) => api.put(`/organizations/${id}`, data);

// Quick lookup for dropdowns (minimal data)
export const getAircraftDropdown = (api) => api.get('/aircraft', { params: { limit: 500 } });
export const getEnginesDropdown = (api) => api.get('/engines', { params: { limit: 500 } });
export const getComponentsDropdown = (api) => api.get('/components', { params: { limit: 500 } });

export default createApiClient;
